import { AlertTriangleIcon, RefreshCwIcon } from 'lucide-react';
import React, { ReactNode, Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { Alert, AlertDescription } from './ui/alert';
import { Button } from './ui/button';

interface ComponentBoundaryProps {
  children: ReactNode;
  fallback: ReactNode;
  errorFallback?: ReactNode;
  onError?: (error: Error, errorInfo: { componentStack?: string | null }) => void;
  name?: string; // Component name for better error tracking
}

/**
 * Default error fallback component with retry functionality
 */
function DefaultErrorFallback({
  error,
  resetErrorBoundary,
  componentName,
}: {
  error: Error;
  resetErrorBoundary: () => void;
  componentName?: string;
}) {
  return (
    <Alert variant="destructive" className="m-4">
      <AlertTriangleIcon className="h-4 w-4" />
      <AlertDescription className="flex items-center justify-between">
        <div>
          <p className="font-medium">{componentName ? `Error in ${componentName}` : 'Something went wrong'}</p>
          <p className="text-sm text-muted-foreground mt-1">{error.message || 'An unexpected error occurred'}</p>
        </div>
        <Button variant="outline" size="sm" onClick={resetErrorBoundary} className="ml-4">
          <RefreshCwIcon className="h-3 w-3 mr-1" />
          Retry
        </Button>
      </AlertDescription>
    </Alert>
  );
}

/**
 * ComponentBoundary - Generic wrapper for Suspense and ErrorBoundary
 *
 * ⚠️ IMPORTANT: This generic wrapper is less ideal than component-specific boundaries.
 * Each component should ideally have its own contextual loading/error UI that matches
 * its expected size, shape, and context.
 *
 * @example ✅ PREFERRED: Component-specific boundaries (like reviewers-list.tsx):
 * ```tsx
 * function ReviewersList() {
 *   return (
 *     <ErrorBoundary
 *       fallback={
 *         <div className="p-2 text-center text-muted-foreground">
 *           <p>Unable to load reviewers</p>
 *           <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
 *             Retry
 *           </Button>
 *         </div>
 *       }
 *     >
 *       <Suspense
 *         fallback={
 *           <div className="flex items-center px-2">
 *             <Skeleton className="w-24 h-8 rounded-full overflow-hidden" />
 *           </div>
 *         }
 *       >
 *         <ReviewersListInner />
 *       </Suspense>
 *     </ErrorBoundary>
 *   );
 * }
 * ```
 *
 * @example ⚠️ GENERIC WRAPPER: Use only when contextual boundaries aren't feasible:
 * ```tsx
 * function MyComponent() {
 *   return (
 *     <ComponentBoundary
 *       fallback={<Skeleton className="h-8 w-full" />}
 *       name="MyComponent"
 *     >
 *       <MyComponentInner />
 *     </ComponentBoundary>
 *   );
 * }
 * ```
 *
 * Why component-specific boundaries are better:
 * - Loading skeletons match exact component size/shape
 * - Error messages are contextual and helpful
 * - Better isolation and debugging
 * - Self-contained components
 */
export function ComponentBoundary({ children, fallback, errorFallback, onError, name }: ComponentBoundaryProps) {
  const handleError = (error: Error, errorInfo: any) => {
    // Log error for development and monitoring
    if (import.meta.env.DEV) {
      console.error(`Error in ${name || 'Component'}:`, error);
      console.error('Component stack:', errorInfo.componentStack);
    }

    // Call custom error handler if provided
    onError?.(error, errorInfo);

    // In production, you might want to send to error tracking service
    // Example: Sentry.captureException(error, { tags: { component: name } });
  };

  const errorFallbackComponent = errorFallback || (
    <DefaultErrorFallback error={new Error('Component error')} resetErrorBoundary={() => {}} componentName={name} />
  );

  return (
    <ErrorBoundary
      FallbackComponent={({ error, resetErrorBoundary }) =>
        errorFallback || (
          <DefaultErrorFallback error={error} resetErrorBoundary={resetErrorBoundary} componentName={name} />
        )
      }
      onError={handleError}
    >
      <Suspense fallback={fallback}>{children}</Suspense>
    </ErrorBoundary>
  );
}

/**
 * Convenience wrapper for components that only need Suspense (no error boundary)
 */
export function SuspenseBoundary({ children, fallback }: { children: ReactNode; fallback: ReactNode }) {
  return <Suspense fallback={fallback}>{children}</Suspense>;
}

/**
 * Convenience wrapper for components that only need Error boundary (no suspense)
 */
export function ErrorOnlyBoundary({
  children,
  errorFallback,
  onError,
  name,
}: {
  children: ReactNode;
  errorFallback?: ReactNode;
  onError?: (error: Error, errorInfo: any) => void;
  name?: string;
}) {
  const handleError = (error: Error, errorInfo: any) => {
    if (import.meta.env.DEV) {
      console.error(`Error in ${name || 'Component'}:`, error);
    }
    onError?.(error, errorInfo);
  };

  return (
    <ErrorBoundary
      FallbackComponent={({ error, resetErrorBoundary }) =>
        errorFallback || (
          <DefaultErrorFallback error={error} resetErrorBoundary={resetErrorBoundary} componentName={name} />
        )
      }
      onError={handleError}
    >
      {children}
    </ErrorBoundary>
  );
}
